// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { AlertCircle, Loader2, Building2 } from 'lucide-react'
import PasswordField from '../components/PasswordField'
import { useAuth } from '../auth/AuthContext'
import { resolvePharmacyFromUrl, safeNextPath } from '../auth/pharmacyUrl'
import * as api from '../api/client'
import MfaCodePrompt from '../components/MfaCodePrompt'
import MfaEnrollPrompt from '../components/MfaEnrollPrompt'

interface Props { onScreenEnter: () => void }

// Friendly mapping for the error codes the backend can return
function loginErrorMessage(code: string): string {
  switch (code) {
    case 'invalid_credentials':
      return 'That email and password don’t match an active account.'
    case 'account_locked':
      return 'This account is locked due to too many failed login attempts. Try again later or contact your administrator.'
    case 'account_disabled':
      return 'This account has been disabled. Contact your administrator.'
    case 'account_pending':
      return 'This account hasn’t finished setup yet. Check your email for the invitation link.'
    case 'email_password_required':
      return 'Please enter both your email and password.'
    case 'rate_limited':
      return 'Too many attempts. Please wait a moment and try again.'
    case 'sso_required':
      // AUTH-1: the backend blocks password login once a pharmacy sets require_sso. Without this
      // case the generic /login page fell through to "Sign-in failed. Please try again.", which
      // reads as an outage — the user is never told to use their pharmacy's SSO link instead.
      // PharmacyLoginPage already handles this; the branded page has the SSO button, this one may not.
      return 'Your pharmacy requires single sign-on. Use your pharmacy’s sign-in link (or the single sign-on button below) instead of a password.'
    default:
      return 'Sign-in failed. Please try again.'
  }
}

export default function LoginPage({ onScreenEnter }: Props) {
  useEffect(() => { document.title = 'Sign in — OwlRx' }, [])
  const { me, loading, signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mfaToken, setMfaToken] = useState<string | null>(null)
  const [mfaEnroll, setMfaEnroll] = useState(false)

  // WI-895: detect per-pharmacy URL and fetch branding for the login page.
  // If subdomain or /p/{slug}/ matches a real pharmacy, customize header.
  // Falls back to generic OwlRx branding when no pharmacy or 404.
  const [branding, setBranding] = useState<api.PharmacyBranding | null>(null)
  const [brandingError, setBrandingError] = useState<string | null>(null)
  const [platformSso, setPlatformSso] = useState(false)
  const [ssoRequired, setSsoRequired] = useState(false)
  useEffect(() => { api.getPublicPlatformSso().then(r => { setPlatformSso(r.enabled); setSsoRequired(!!r.require_sso) }).catch(() => {}) }, [])
  useEffect(() => {
    const resolved = resolvePharmacyFromUrl()
    if (!resolved.slug) return
    let cancelled = false
    api.getPharmacyBranding(resolved.slug)
      .then(b => { if (!cancelled) setBranding(b) })
      .catch(err => {
        if (cancelled) return
        // 404 just means the slug doesn't match a real pharmacy; fall back
        // to generic. Surface other errors silently in console.
        const msg = err instanceof Error ? err.message : String(err)
        if (msg.includes('pharmacy_not_found') || msg.includes('pharmacy_archived')) {
          setBrandingError('not_found')
        } else {
          // Network or server hiccup — log but render generic.
          console.warn('[WI-895] branding fetch failed:', msg)
        }
      })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    onScreenEnter()
  }, [onScreenEnter])

  // If already signed in, send them where they were going, else into their pharmacy workspace.
  // When the login happened on a per-pharmacy URL (/p/{code}/login), redirect to that pharmacy's
  // workspace root /p/{code} rather than '/', so the pharmacy context (and branding/routing) is kept
  // — otherwise a freshly-authenticated pharmacy user lands on the generic platform home. This matters
  // after forced MFA enrollment too: the post-enroll /me refresh triggers this same effect.
  useEffect(() => {
    if (loading) return
    if (me?.authenticated) {
      const from = (location.state as any)?.from as { pathname?: string; search?: string } | undefined
      const fromPath = from?.pathname ? from.pathname + (from.search ?? '') : undefined
      const pharmaCode = resolvePharmacyFromUrl().slug
      // NEXT-1: ?next survives the hard redirects that drop router state (validated path).
      const next = safeNextPath(new URLSearchParams(window.location.search).get('next'))
      const dest = fromPath ?? next ?? (pharmaCode ? `/p/${pharmaCode}` : '/')
      navigate(dest, { replace: true })
    }
  }, [loading, me, navigate, location.state])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) return
    setError(null)

    if (!email.trim() || !password) {
      setError(loginErrorMessage('email_password_required'))
      return
    }

    setSubmitting(true)
    const result = await signIn(email.trim(), password)
    setSubmitting(false)
    if (result.mfaRequired && result.mfaToken) {
      setMfaToken(result.mfaToken)   // show the second-factor prompt
      return
    }
    if (result.mfaEnrollmentRequired && result.mfaToken) {
      setMfaToken(result.mfaToken); setMfaEnroll(true)   // forced enrollment
      return
    }
    if (!result.ok) {
      setError(loginErrorMessage(result.error ?? 'login_failed'))
    }
    // success → useEffect above will redirect
  }

  return (
    <main style={{ maxWidth: 440, margin: '0 auto', padding: '32px 24px' }}>
      {/* WI-895: branded header for per-pharmacy login URLs */}
      {branding ? (
        <div style={brandedHeaderStyle}>
          {branding.logo_url ? (
            <img
              src={branding.logo_url}
              alt={`${branding.display_name} logo`}
              style={brandedLogoStyle}
            />
          ) : (
            <div style={{
              ...brandedLogoStyle,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: branding.primary_color_hex ?? 'var(--color-primary, #0d9488)',
              color: '#fff',
            }}>
              <Building2 size={32} aria-hidden="true" />
            </div>
          )}
          <h1 style={titleStyle}>{branding.display_name}</h1>
          <p style={subtitleStyle}>
            Sign in to your pharmacy portal.
            {branding.sso_provider !== 'none' && (
              <> Single sign-on is configured for this pharmacy.</>
            )}
          </p>
        </div>
      ) : (
        <>
          <h1 style={titleStyle}>Sign in</h1>
          <p style={subtitleStyle}>
            {brandingError === 'not_found'
              ? <>This pharmacy URL isn't recognized. You can still sign in with your OwlRx credentials below.</>
              : <>Use the credentials your administrator gave you. If you received an
                invitation email, follow the link in that email instead of signing in here.</>
            }
          </p>
        </>
      )}

      {mfaToken ? (
        mfaEnroll
          ? <MfaEnrollPrompt mfaToken={mfaToken} email={email} onSuccess={() => { /* /me refresh triggers redirect */ }} />
          : <MfaCodePrompt mfaToken={mfaToken} email={email} onSuccess={() => { /* /me refresh triggers the redirect effect */ }} />
      ) : (
      <>
      {!ssoRequired && (<form onSubmit={handleSubmit} style={formStyle} noValidate>
        <label style={labelStyle} htmlFor="login-email">Email</label>
        <input
          id="login-email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={submitting}
          required
          style={inputStyle}
        />

        <label style={labelStyle} htmlFor="login-password">Password</label>
        <PasswordField
          id="login-password"
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
          disabled={submitting}
          required
          style={inputStyle}
        />

        {error && (
          <div role="alert" style={errorStyle}>
            <AlertCircle size={16} aria-hidden="true" style={{ flexShrink: 0 }} />
            <span>{error}</span>
          </div>
        )}

        <button type="submit" disabled={submitting} style={submitStyle}>
          {submitting ? (
            <><Loader2 size={16} className="spin" aria-hidden="true" /> Signing in…</>
          ) : (
            'Sign in'
          )}
        </button>
      </form>)}

      {(platformSso || ssoRequired) && (
        <>
          {!ssoRequired && <div style={{ textAlign: 'center', margin: '18px 0', color: 'var(--color-text-mute)', fontSize: 12 }}>or</div>}
          <button
            type="button"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, width: '100%', padding: '11px 16px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            onClick={() => { try { sessionStorage.setItem('owlrx_sso_pending', '1') } catch {}; window.location.href = api.platformSsoStartUrl(window.location.origin + '/') }}
          >
            <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true" style={{ flexShrink: 0 }}>
              <rect x="1" y="1" width="9" height="9" fill="#f25022" /><rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" /><rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            Employee login
          </button>
        </>
      )}

      </>
      )}

      <div style={altLinksStyle}>
        <Link to="/forgot-password" style={linkStyle}>Forgot password?</Link>
        <span aria-hidden="true" style={{ color: 'var(--color-text-mute)' }}>•</span>
        <Link to="/accept-invitation" style={linkStyle}>I have an invitation</Link>
      </div>

      <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--color-border)', textAlign: 'center', fontSize: 12, lineHeight: 1.6, color: 'var(--color-text-muted)' }}>
        <div style={{ marginBottom: 4 }}>
          This system processes protected health information. Authorized use only; activity may be monitored and audited.
        </div>
        <div style={{ marginBottom: 4 }}>
          © {new Date().getFullYear()} Owl Consultations — proprietary licensed software. Unauthorized
          access or use is prohibited and may be referred for civil and criminal prosecution.
        </div>
        <Link to="/legal/privacy" style={linkStyle}>Privacy Policy</Link>
        {' · '}
        <Link to="/legal/terms" style={linkStyle}>Terms of Use</Link>
        {' · '}
        <Link to="/legal/data-rights" style={linkStyle}>Your Data Rights</Link>
      </div>
    </main>
  )
}

// ---------- styles ----------
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 32, fontWeight: 700, margin: '0 0 8px', letterSpacing: -0.5,
}

const subtitleStyle: React.CSSProperties = {
  margin: '0 0 28px',
  color: 'var(--color-text-soft)',
  fontSize: 14,
  lineHeight: 1.5,
}

const formStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 6,
}

const labelStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 500,
  color: 'var(--color-text)',
  marginTop: 12,
}

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  fontSize: 14, fontFamily: 'inherit',
  background: 'var(--color-surface)',
}

const errorStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  marginTop: 12, padding: '8px 12px',
  background: '#fee2e2',
  border: '1px solid #fecaca',
  borderRadius: 4,
  color: '#991b1b',
  fontSize: 13, lineHeight: 1.4,
}

const submitStyle: React.CSSProperties = {
  marginTop: 18,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  padding: '12px 18px',
  background: 'var(--color-primary, #047857)',
  color: '#fff',
  border: 'none', borderRadius: 6,
  fontSize: 14, fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const altLinksStyle: React.CSSProperties = {
  display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center',
  marginTop: 24, fontSize: 13,
}

const linkStyle: React.CSSProperties = {
  color: 'var(--color-primary, #047857)',
  textDecoration: 'none',
}

// WI-895 branded header styles
const brandedHeaderStyle: React.CSSProperties = {
  textAlign: 'center',
  marginBottom: 24,
  marginTop: 8,
}

const brandedLogoStyle: React.CSSProperties = {
  width: 64,
  height: 64,
  borderRadius: 12,
  objectFit: 'contain',
  marginBottom: 12,
}
